import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const stats = [
  { value: '15,000+', label: 'Patients Served' },
  { value: '25+', label: 'Specialists' },
  { value: '15 Yrs', label: 'Experience' },
];

function renderHomeIcon(id) {
  const props = {
    width: 24,
    height: 24,
    strokeWidth: 2,
    stroke: 'var(--teal-primary)',
    fill: 'none',
    style: { flexShrink: 0, display: 'block' }
  };
  switch (id) {
    case 'expert':
      return (
        <svg viewBox="0 0 24 24" {...props} width="32" height="32">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'lab':
      return (
        <svg viewBox="0 0 24 24" {...props} width="32" height="32">
          <path d="M4.5 16.5c-1.5 1.26-2 3-2 3h19s-.5-1.74-2-3L15 12V3h-6v9L4.5 16.5z" />
          <line x1="6" y1="3" x2="18" y2="3" />
          <line x1="9.5" y1="11" x2="14.5" y2="11" />
        </svg>
      );
    case 'patient':
      return (
        <svg viewBox="0 0 24 24" {...props} width="32" height="32">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      );
    case 'price':
      return (
        <svg viewBox="0 0 24 24" {...props} width="32" height="32">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      );
    default:
      return null;
  }
}

const reasons = [
  { iconId: 'expert', title: 'Expert Specialists', desc: 'Our team comprises internationally trained and certified doctors with years of clinical experience.' },
  { iconId: 'lab', title: 'State-of-the-Art Labs', desc: 'Equipped with advanced diagnostic automation for fast, ISO-certified medical testing.' },
  { iconId: 'patient', title: 'Patient-First Ethics', desc: 'Compassionate care is at the heart of everything we do. We prioritize your dignity and comfort.' },
  { iconId: 'price', title: 'Affordable Care', desc: 'Premium medical services priced accessibly to serve all sections of our community.' }
];

const previewServices = [
  { image: '/images/eyecare_service.png', title: 'Eye Care', desc: 'Comprehensive vision checks, advanced cataract surgery, and glaucoma management.', path: '/services#eye-care' },
  { image: '/images/maternity_service.png', title: 'Maternity Care', desc: 'Full-term prenatal care, high-risk pregnancy care, normal and C-section delivery.', path: '/services#maternity' },
  { image: '/images/pediatric_service.png', title: 'Pediatrician', desc: 'Newborn check-ups, developmental milestones tracking, and child immunization.', path: '/services#pediatrician' },
  { image: '/images/cosmetic_gynae_service.png', title: 'Cosmetic Gynae', desc: 'Discreet and expert aesthetic gynecology procedures by female board specialists.', path: '/services#cosmetic-gynae' },
  { image: '/images/ent_service.png', title: 'ENT Care', desc: 'Hearing assessments, allergy relief, sinus treatments, and micro-ear surgeries.', path: '/services#ent' },
  { image: '/images/dental_service.png', title: 'Dental Clinic', desc: 'Scaling, root canals, dental implants, crowns, and child-friendly dentistry.', path: '/services#dental' }
];

function Home() {
  const [gallery, setGallery] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadHomeData() {
      try {
        const [galleryRes, eventsRes] = await Promise.all([
          api.get('/gallery'),
          api.get('/events'),
        ]);
        setGallery(galleryRes.data.slice(0, 3));
        setEvents(eventsRes.data.slice(0, 3));
      } catch (error) {
        console.warn('Failed to load home dynamic content:', error);
      } finally {
        setLoading(false);
      }
    }
    loadHomeData();
  }, []);

  return (
    <>
      {/* Hero Banner */}
      <section className="hero" style={{ backgroundImage: 'linear-gradient(rgba(8, 50, 62, 0.85), rgba(8, 50, 62, 0.85)), url(/images/hero_home.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container hero-grid">
          <div>
            <div className="eyebrow" style={{ color: 'var(--gold-light)' }}>✦ Trusted Healthcare Since 2022</div>
            <h1>Your Health Deserves <em>Expert</em> Care</h1>
            <p className="subtitle" style={{ color: 'rgba(255, 255, 255, 0.88)', marginBottom: '32px' }}>
              Mega Health Group brings together specialists in Eye Care, Maternity, Pediatrics, ENT, Dental, and Cosmetic Gynecology — all under one trusted roof.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 40 }}>
              <Link to="/contact" className="btn btn-gold">Book Appointment</Link>
              <Link to="/services" className="btn btn-secondary" style={{ color: 'white', borderColor: 'white', background: 'transparent' }}>Explore Services</Link>
            </div>
            
            <div className="hero-stats">
              {stats.map(stat => (
                <div key={stat.label} className="hero-stat">
                  <div className="hero-stat-num">{stat.value}</div>
                  <div className="hero-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="hero-card">
            <h3 style={{ color: 'var(--teal-dark)', display: 'flex', alignItems: 'center', gap: 8, fontSize: '1.4rem' }}>
              Our Medical Specialties
            </h3>
            <p style={{ fontSize: '0.9rem', marginBottom: '20px' }}>Click to explore our main clinical departments:</p>
            <div className="service-chips">
              <Link to="/services#eye-care" className="chip">Eye Care</Link>
              <Link to="/services#maternity" className="chip">Maternity Care</Link>
              <Link to="/services#pediatrician" className="chip">Pediatrician</Link>
              <Link to="/services#cosmetic-gynae" className="chip">Cosmetic Gynae</Link>
              <Link to="/services#ent" className="chip">ENT Care</Link>
              <Link to="/services#dental" className="chip">Dental Clinic</Link>
              <Link to="/blood-test" className="chip">Blood Tests</Link>
              <Link to="/blood-test#imaging" className="chip">Ultrasound Scan</Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-pad" style={{ background: 'var(--white)' }}>
        <div className="container">
          <div className="grid-2">
            <div style={{ position: 'relative' }}>
              <img src="/images/clinic_main.png" alt="Mega Health Group Clinic" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)' }} />
              <div style={{ position: 'absolute', bottom: -20, right: 20, background: 'var(--gold-primary)', color: 'white', padding: '16px 24px', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' }}>
                <div style={{ fontSize: '1.8rem', fontWeight: 700, fontFamily: 'Playfair Display, serif', lineHeight: 1 }}>15+</div>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Years Excellence</div>
              </div>
            </div>
            <div>
              <div className="eyebrow">About Mega Health Group</div>
              <h2>A Legacy of Compassionate Healthcare</h2>
              <p style={{ marginTop: '16px' }}>
                Founded in 2022, Mega Health Group has grown into a trusted multi-specialty healthcare provider serving thousands of patients and their families. Our mission is simple: deliver world-class medical care with warmth, dignity, and accuracy.
              </p>
              <p>
                We believe that access to quality healthcare is a fundamental right. Our team of experienced specialists works tirelessly to provide precise diagnoses, effective treatments, and genuine compassion at every single visit.
              </p>
              <ul className="check-list" style={{ margin: '24px 0', gridTemplateColumns: '1fr 1fr', display: 'grid' }}>
                <li>Internationally Trained Doctors</li>
                <li>State-of-the-Art Equipment</li>
                <li>Patient-First Approach</li>
                <li>Affordable Quality Care</li>
                <li>ISO Certified Diagnostics</li>
                <li>Confidential & Caring</li>
              </ul>
              <Link to="/about" className="btn btn-primary">Learn More About Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-pad" style={{ background: 'var(--bg-light)' }}>
        <div className="container">
          <div className="section-header center">
            <div className="eyebrow">What We Treat</div>
            <h2>Our Medical Specialties</h2>
            <p>From routine health checks to specialized treatments, our expert team delivers exceptional care across six core disciplines.</p>
          </div>
          
          <div className="grid-3">
            {previewServices.map(service => (
              <div key={service.title} className="card" style={{ padding: '0 0 24px 0', overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: 'var(--radius-lg)' }}>
                <img src={service.image} alt={service.title} loading="lazy" style={{ width: '100%', height: '200px', objectFit: 'cover', marginBottom: 16 }} />
                <div style={{ padding: '0 24px' }}>
                  <h3 style={{ margin: '8px 0' }}>{service.title}</h3>
                  <p style={{ fontSize: '0.92rem', minHeight: 70, margin: '10px 0', lineHeight: '1.6', color: 'var(--muted)' }}>{service.desc}</p>
                  <Link to={service.path} style={{ color: 'var(--teal-primary)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    Learn More →
                  </Link>
                </div>
              </div>
            ))}
          </div>
          
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Link to="/services" className="btn btn-secondary">View All Services</Link>
          </div>
        </div>
      </section>

      {/* Blood Test & Ultrasound Preview Banner */}
      <section className="section-pad" style={{ backgroundImage: 'linear-gradient(rgba(8, 50, 62, 0.9), rgba(8, 50, 62, 0.9)), url(/images/lab_diagnostics.png)', backgroundSize: 'cover', backgroundPosition: 'center', color: 'white' }}>
        <div className="container">
          <div className="grid-2">
            <div>
              <div className="eyebrow" style={{ color: 'var(--gold-light)' }}>Diagnostics Wing</div>
              <h2 style={{ color: 'white' }}>ISO-Certified Laboratory & Ultrasound Imaging<br/><span style={{ fontSize: '1rem', fontWeight: 'normal', opacity: 0.9, display: 'block', marginTop: '8px' }}>(Operating Under the Trade Name: Mega Lab & Digno Research Center)</span></h2>
              <p style={{ color: 'rgba(255,255,255,0.85)', margin: '16px 0 24px' }}>
                Fast, highly accurate diagnostic reports generated in-house. We offer complete hematology panels, hormonal assays, and high-resolution obstetric & pelvic ultrasound mapping.
              </p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Link to="/blood-test" className="btn btn-gold">View Test Catalog</Link>
                <Link to="/contact" className="btn btn-secondary" style={{ color: 'white', borderColor: 'white', background: 'transparent' }}>Book Test / Scan</Link>
              </div>
            </div>
            
            <div className="card" style={{ background: 'rgba(255, 255, 255, 0.08)', borderColor: 'rgba(255, 255, 255, 0.12)', color: 'white' }}>
              <h3 style={{ color: 'white', fontSize: '1.35rem' }}>Fast Turnaround Lab</h3>
              <ul style={{ listStyle: 'none', display: 'grid', gap: 14, margin: '20px 0 0' }}>
                <li style={{ display: 'flex', gap: 12 }}><span style={{ color: 'var(--gold-light)' }}>✦</span> Results in 4–6 Hours for routine tests</li>
                <li style={{ display: 'flex', gap: 12 }}><span style={{ color: 'var(--gold-light)' }}>✦</span> In-house Female Sonologist on request</li>
                <li style={{ display: 'flex', gap: 12 }}><span style={{ color: 'var(--gold-light)' }}>✦</span> High-resolution broad-band probes & color doppler</li>
                <li style={{ display: 'flex', gap: 12 }}><span style={{ color: 'var(--gold-light)' }}>✦</span> Home sample collection available</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-pad" style={{ background: 'var(--white)' }}>
        <div className="container">
          <div className="section-header center">
            <div className="eyebrow">The Mega Difference</div>
            <h2 style={{ marginBottom: '8px' }}>Why Choose Mega Health Group?</h2>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--teal-primary)', marginBottom: '16px' }}>(MEGA HOSPITAL FOUNDATION)</div>
            <p>We know you have options. Here is what makes us worth trusting with your most important asset — your health.</p>
          </div>
          
          <div className="why-grid">
            {reasons.map(reason => (
              <div key={reason.title} className="why-card">
                <div className="icon" style={{ height: 32, display: 'flex', alignItems: 'center' }}>{renderHomeIcon(reason.iconId)}</div>
                <h4 style={{ fontSize: '1.2rem', marginTop: 12, marginBottom: 8 }}>{reason.title}</h4>
                <p style={{ fontSize: '0.88rem', color: 'var(--muted)', marginBottom: 0 }}>{reason.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Objects of Mega Health Group */}
      <section className="section-pad" style={{ background: 'var(--bg-light)' }}>
        <div className="container">
          <div className="section-header center">
            <div className="eyebrow">Our Purpose</div>
            <h2>Objects of Mega Health Group</h2>
          </div>
          <div style={{ maxWidth: '900px', margin: '0 auto', background: 'var(--white)', padding: '40px', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)' }}>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--muted)', marginBottom: '32px' }}>
              Mega Health Group (Mega Hospital Foundation), established in 2022, is a charitable healthcare Trust / Section 8 Company formed with the objective of promoting, supporting, and delivering accessible, ethical, and high-quality medical services for the benefit of the public at large.
            </p>
            <h3 style={{ color: 'var(--teal-dark)', marginBottom: '20px', fontSize: '1.2rem' }}>PRIMARY OBJECTS OF THE MEGA HEALTH GROUP</h3>
            <ol style={{ paddingLeft: '24px', color: 'var(--muted)', lineHeight: '1.8', fontSize: '1rem', display: 'flex', flexDirection: 'column', gap: '16px', margin: 0 }}>
              <li><strong>To establish, maintain, manage, and operate</strong> hospitals, clinics, diagnostic centers, and other healthcare facilities for providing medical treatment and healthcare services to the general public.</li>
              <li><strong>To provide affordable and accessible healthcare services</strong> to individuals and families without discrimination on the basis of caste, creed, religion, gender, or socio-economic status.</li>
              <li><strong>To promote preventive healthcare</strong>, medical awareness, and public health education within the community.</li>
              <li><strong>To provide consultation, diagnosis, treatment, and rehabilitation services</strong> across various medical specialties through qualified and experienced healthcare professionals.</li>
              <li><strong>To undertake charitable medical initiatives</strong>, including free or subsidized treatment for economically weaker and underserved sections of society.</li>
              <li><strong>To collaborate</strong> with government authorities, non-governmental organizations, and other institutions for the advancement and strengthening of healthcare services.</li>
              <li><strong>To ensure the delivery of healthcare services</strong> with dignity, compassion, integrity, and strict adherence to ethical and professional medical standards.</li>
              <li><strong>To continuously improve the quality of healthcare services</strong> through training, research, innovation, and the adoption of modern medical practices and technologies.</li>
            </ol>
          </div>
        </div>
      </section>

      {/* Gallery & Events dynamic sections */}
      {!loading && (gallery.length > 0 || events.length > 0) && (
        <section className="section-pad" style={{ background: 'var(--white)' }}>
          <div className="container">
            {/* Gallery Section */}
            {gallery.length > 0 && (
              <div style={{ marginBottom: 60 }}>
                <div className="section-header center">
                  <div className="eyebrow">Our Moments</div>
                  <h2>Recent Activities</h2>
                  <p>A window into our facility, our medical team, and community outreach.</p>
                </div>
                <div className="gallery-masonry">
                  {gallery.map(item => (
                    <div key={item._id} className="gallery-card">
                      <div className="gallery-thumb">
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.title} />
                        ) : (
                          <span style={{ fontSize: '3rem' }}>{item.emoji || '📸'}</span>
                        )}
                      </div>
                      <div className="gallery-card-body">
                        <div className="date">{item.category}</div>
                        <h4>{item.title}</h4>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: 'center', marginTop: 24 }}>
                  <Link to="/media" className="btn btn-outline btn-sm">View Full Gallery</Link>
                </div>
              </div>
            )}

            {/* Events Section */}
            {events.length > 0 && (
              <div>
                <div className="section-header center">
                  <div className="eyebrow">Events Calendar</div>
                  <h2>Upcoming Health Activities</h2>
                  <p>Join our free health screenings, webinars, and pediatric awareness campaigns.</p>
                </div>
                <div className="events-grid">
                  {events.map(event => (
                    <div key={event._id} className="event-card">
                      <div className="event-img">
                        {event.imageUrl ? (
                          <img src={event.imageUrl} alt={event.title} />
                        ) : (
                          <span>{event.emoji || '📅'}</span>
                        )}
                        <div className="event-date-badge">{event.date}</div>
                      </div>
                      <div className="event-body">
                        <div className="event-meta">
                          <span>📍 {event.location || 'Mega Health Group'}</span>
                        </div>
                        <h4>{event.title}</h4>
                        <p style={{ fontSize: '0.88rem', color: 'var(--muted)', minHeight: 60 }}>
                          {event.description}
                        </p>
                        <Link to="/contact" className="event-link">Register Free →</Link>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: 'center', marginTop: 24 }}>
                  <Link to="/media?tab=events" className="btn btn-outline btn-sm">View All Events</Link>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Book Appointment CTA Band */}
      <section className="section-pad" style={{ backgroundImage: 'linear-gradient(rgba(8, 50, 62, 0.9), rgba(8, 50, 62, 0.9)), url(/images/hero_home.png)', backgroundSize: 'cover', backgroundPosition: 'center', color: 'white', textAlign: 'center' }}>
        <div className="container">
          <div className="eyebrow" style={{ color: 'var(--gold-light)' }}>Get in touch</div>
          <h2 style={{ color: 'white', marginBottom: 16 }}>Ready to Book Your Appointment?</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', maxWidth: '600px', margin: '0 auto 30px' }}>
            Connect with our clinical desk to arrange your consultation, diagnostics test, or physical check-up today.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-gold">Book Appointment Now</Link>
            <Link to="/donate" className="btn btn-secondary" style={{ color: 'white', borderColor: 'white', background: 'transparent' }}>Support Our Charity Camps</Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
